import clsx from 'clsx';

import {
  BuyerIntentLeadClick,
  normalizeBuyerIntentId,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import ClickoutWrapper from 'components/ClickoutWrapper';
import CtaButton from 'components/CtaButton';
import { useEventContext } from 'context/EventContext';

import classes from './Main.module.scss';

/** @type {import("./types").default} */
const Main = ({
  listing,
  className,
  children,
  endIcon,
  onShowPricingModal,
  hasDemoForm,
  listingPosition,
  enableBuyerIntent = false,
  showPriceCta = false,
}) => {
  const { addEvent } = useEventContext({
    engagementComponent: 'product_card-item',
  });
  const buyerIntentProductId = normalizeBuyerIntentId(
    listing.global_product_id
  );

  const highlyViewedEventString = listing.most_visited
    ? ' | highly-viewed'
    : '';

  const onClickPricing = () =>
    onShowPricingModal &&
    onShowPricingModal({ pGuid: listing.global_product_id });

  // A "Learn more" card (non-premium, no demo form) becomes a "Get Price" CTA
  // when the category is eligible for the PPL price form.
  const isLearnMoreCard = !listing.is_premium && !hasDemoForm;
  if (showPriceCta && isLearnMoreCard) {
    // Instrument missing buyer-intent IDs the same way the regular get-price
    // branch below does, so data gaps surface in prod telemetry for this path.
    if (enableBuyerIntent && !buyerIntentProductId) {
      warnMissingBuyerIntentIds('listing/main:price-cta', {
        productId: listing.global_product_id,
      });
    }

    const priceButton = (
      <CtaButton
        className={clsx('Main', className)}
        data-testid="listing-item_main_cta"
        fullWidth={true}
        onClick={onClickPricing}
        // `engagementComponent: 'product-card'` (dash) intentionally diverges
        // from the GetApp default `product_card-item` (underscore) applied by
        // the ListingCard EventContext. It matches SoftwareAdvice's canonical
        // `GA4_COMPONENTS.PROD_CARD` so the `ppl_cta` event can be joined
        // cross-brand in GA4 analysis (see DMROIPPL-79).
        {...addEvent({
          productClickPosition: listingPosition,
          engagementComponent: 'product-card',
          engagementDetails: 'button_price',
          engagementDestination: 'go-to_ppl-form',
          name: 'ppl_cta',
        })}
      >
        Get Price
      </CtaButton>
    );

    return enableBuyerIntent && buyerIntentProductId ? (
      <BuyerIntentLeadClick
        productId={buyerIntentProductId}
        sourceLocation="listing/main:price-cta"
      >
        {priceButton}
      </BuyerIntentLeadClick>
    ) : (
      priceButton
    );
  }

  if (
    !listing.is_premium &&
    hasDemoForm &&
    enableBuyerIntent &&
    !buyerIntentProductId
  ) {
    warnMissingBuyerIntentIds('listing/main:get-price', {
      productId: listing.global_product_id,
    });
  }

  return !listing.is_premium && hasDemoForm ? (
    !enableBuyerIntent || !buyerIntentProductId ? (
      <CtaButton
        className={clsx('Main', className)}
        data-testid="listing-item_main_cta"
        fullWidth={true}
        onClick={onClickPricing}
        {...addEvent({
          engagementDetails: 'button_get_price' + highlyViewedEventString,
          engagementDestination: 'go-to_ppl-price-form',
          name: 'engagement_product_click',
        })}
      >
        get price
      </CtaButton>
    ) : (
      <BuyerIntentLeadClick
        productId={buyerIntentProductId}
        sourceLocation="listing/main:get-price"
      >
        <CtaButton
          className={clsx('Main', className)}
          data-testid="listing-item_main_cta"
          fullWidth={true}
          onClick={onClickPricing}
          {...addEvent({
            engagementDetails: 'button_get_price' + highlyViewedEventString,
            engagementDestination: 'go-to_ppl-price-form',
            name: 'engagement_product_click',
          })}
        >
          get price
        </CtaButton>
      </BuyerIntentLeadClick>
    )
  ) : (
    <ClickoutWrapper
      listingPosition={listingPosition}
      url={listing.cta_urls.cta}
      isClickout={listing.is_premium}
      buyerIntentProductId={
        enableBuyerIntent ? buyerIntentProductId : undefined
      }
      buyerIntentSourceLocation={
        enableBuyerIntent ? 'listing/main:main-cta' : undefined
      }
      buyerIntentTrackAdClick={enableBuyerIntent && listing.is_premium}
      className={classes.root}
      {...addEvent(
        listing.is_premium
          ? {
              productClickPosition: listingPosition,
              engagementDetails:
                'button_visit-website' + highlyViewedEventString,
              engagementDestination: 'go-to_vendor-website',
              name: 'engagement_product_clickout',
            }
          : {
              productClickPosition: listingPosition,
              engagementDetails: 'button_learn-more' + highlyViewedEventString,
              engagementDestination: 'go-to_product_details',
              name: 'engagement_product_click',
            }
      )}
    >
      <CtaButton
        className={clsx('Main', className)}
        data-testid="listing-item_main_cta"
        component="span"
        fullWidth={true}
      >
        {listing.is_premium ? 'visit website' : children || 'learn more'}
        {endIcon}
      </CtaButton>
    </ClickoutWrapper>
  );
};

export default Main;
